import {z} from 'zod';

export type GetMyCalendarSelectedUsersApiResponse = /** status 200  */ SelectedUserBody[];
export type SetMyCalendarSelectedUsersApiResponse = /** status 204  */ void;
export type SetMyCalendarSelectedUsersApiArg = {
  setMyCalendarSelectedUsersRequestBody: SetMyCalendarSelectedUsersRequestBody;
};
export type SelectedUserBody = {
  userId: string;
  isHidden: boolean;
  color: string | null;
};

export type SetMyCalendarSelectedUsersRequestBody = {
  selectedUsers: SelectedUserBody[];
};

export const selectedUserBodySchema = z.object({
  userId: z.string(),
  isHidden: z.boolean(),
  color: z.string().nullable(),
});

export const setMyCalendarSelectedUsersRequestBodySchema = z.object({
  selectedUsers: z.array(selectedUserBodySchema),
});

export type OAuth2AuthUrlResponseBody = {
  /** Microsoft OAuth2 authorization URL that the client should redirect to */
  authUrl: string;
};
export type StartOAuth2AuthenticationRequestBody = {
  /** Callback URL (absolute) that Microsoft Graph will redirect to after authentication */
  callbackUrl: string;
};
export type CompleteOAuth2AuthenticationRequestBody = {
  /** Auth code as received from external OAuth2 provider */
  code: string;
  /** Client state string as received from external OAuth2 provider */
  state: string;
  /** Callback URL (absolute) that was sent to the start OAuth2 authentication request */
  callbackUrl: string;
};

export type CompleteOAuth2AuthenticationApiResponse = /** status 204  */ void;
export type CompleteOAuth2AuthenticationApiArg = {
  completeOAuth2AuthenticationRequestBody: CompleteOAuth2AuthenticationRequestBody;
};

export type OAuth2AuthenticationCallbackApiResponse = /** status 204  */ void;
export type OAuth2AuthenticationCallbackApiArg = {
  /** OAuth2 authorization code received from Microsoft Graph */
  code?: string | null;
  /** OAuth2 state parameter for CSRF protection */
  state?: string | null;
  /** OAuth2 error code if authentication failed */
  error?: string | null;
};

export type StartOAuth2AuthenticationApiResponse = /** status 200  */ OAuth2AuthUrlResponseBody;
export type StartOAuth2AuthenticationApiArg = {
  startOAuth2AuthenticationRequestBody: StartOAuth2AuthenticationRequestBody;
};

export const getMyCalendarSelectedUsersResponseSchema = z.array(selectedUserBodySchema);

export const oAuth2AuthUrlResponseBodySchema = z.object({
  authUrl: z.string(),
});

export const startOAuth2AuthenticationRequestBodySchema = z.object({
  callbackUrl: z.string(),
});

export const completeOAuth2AuthenticationRequestBodySchema = z.object({
  code: z.string(),
  state: z.string(),
});

export type OutlookCalendarResponseBody = {
  /** Microsoft Graph calendar identifier */
  id: string;
  /** Display name of the calendar */
  name: string;
  /** Calendar color as provided by Microsoft Graph */
  color?: string | null;
  /** Whether this is the user's default calendar */
  isDefaultCalendar?: boolean | null;
  /** Whether the current user can edit this calendar */
  canEdit?: boolean | null;
};

export type GetMyOutlookCalendarsApiResponse = /** status 200  */ OutlookCalendarResponseBody[];

export const outlookCalendarResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable().optional(),
  isDefaultCalendar: z.boolean().nullable().optional(),
  canEdit: z.boolean().nullable().optional(),
});

export const getMyOutlookCalendarsResponseSchema = z.array(outlookCalendarResponseBodySchema);

export type CreateCalendarIntegrationRequestBody = {
  /** Whether to enable bidirectional synchronization between calendar and DMS tasks */
  bidirectionalSync?: boolean | null;
  /** Array of Microsoft Graph calendar IDs to sync with */
  selectedCalendarIds: string[];
  /** Array of event types to include in synchronization */
  eventTypes?: string[] | null;
  /** Whether to synchronize past events */
  syncPastEvents?: boolean | null;
  /** Number of days to look ahead for event synchronization */
  lookAheadDays?: number | null;
};

export type CreateCalendarIntegrationApiResponse = /** status 204  */ void;
export type CreateCalendarIntegrationApiArg = {
  createCalendarIntegrationRequestBody: CreateCalendarIntegrationRequestBody;
};

export const createCalendarIntegrationRequestBodySchema = z.object({
  bidirectionalSync: z.boolean().nullable().optional(),
  selectedCalendarIds: z.array(z.string()),
  eventTypes: z.array(z.string()).nullable().optional(),
  syncPastEvents: z.boolean().nullable().optional(),
  lookAheadDays: z.number().nullable().optional(),
});

export type CalendarIntegrationResponseBody = {
  /** Unique identifier for the calendar integration */
  id: string;
  /** User ID who owns this calendar integration */
  userId: string;
  /** Current status of the calendar integration */
  status: string;
  /** ISO 8601 timestamp when the integration was created */
  createdAt: string;
  /** ISO 8601 timestamp of the last synchronization attempt */
  lastSyncAt?: string | null;
  /** Error message from the last synchronization attempt, if any */
  lastSyncError?: string | null;
  /** Whether bidirectional synchronization is enabled */
  bidirectionalSync: boolean;
  /** Array of Microsoft Graph calendar IDs being synchronized */
  selectedCalendarIds: string[];
  /** Array of event types included in synchronization */
  eventTypes: string[];
  /** Whether past events are synchronized */
  syncPastEvents: boolean;
  /** Number of days to look ahead for event synchronization */
  lookAheadDays: number;
};

export type GetCalendarIntegrationApiResponse = /** status 200  */ CalendarIntegrationResponseBody;

export const calendarIntegrationResponseBodySchema = z.object({
  id: z.string(),
  userId: z.string(),
  status: z.string(),
  createdAt: z.string(),
  lastSyncAt: z.string().nullable().optional(),
  lastSyncError: z.string().nullable().optional(),
  bidirectionalSync: z.boolean(),
  selectedCalendarIds: z.array(z.string()),
  eventTypes: z.array(z.string()),
  syncPastEvents: z.boolean(),
  lookAheadDays: z.number(),
});

export const getCalendarIntegrationResponseSchema = calendarIntegrationResponseBodySchema;

export type UpdateCalendarIntegrationRequestBody = {
  /** Whether to enable bidirectional synchronization between calendar and DMS tasks */
  bidirectionalSync: boolean;
  /** Array of Microsoft Graph calendar IDs to sync with */
  selectedCalendarIds: string[];
  /** Array of event types to include in synchronization */
  eventTypes: string[];
  /** Whether to synchronize past events */
  syncPastEvents: boolean;
  /** Number of days to look ahead for event synchronization */
  lookAheadDays: number;
};

export type UpdateCalendarIntegrationApiResponse = /** status 204  */ void;
export type UpdateCalendarIntegrationApiArg = {
  integrationId: string;
  updateCalendarIntegrationRequestBody: UpdateCalendarIntegrationRequestBody;
};

export const updateCalendarIntegrationRequestBodySchema = z.object({
  bidirectionalSync: z.boolean(),
  selectedCalendarIds: z.array(z.string()),
  eventTypes: z.array(z.string()),
  syncPastEvents: z.boolean(),
  lookAheadDays: z.number(),
});

export type RevokeCalendarIntegrationApiResponse = /** status 204  */ void;
export type RevokeCalendarIntegrationApiArg = {
  integrationId: string;
};
